import styles from './context-menu.module.scss';
import {
  Button,
  Popover,
  Icon,
  MenuItem,
  MenuProvider,
  Divider,
  IconButton,
  Typography,
  BUTTON_VARIANT,
  BUTTON_SIZE
} from '@sanlam-glacier/app-common/components';
import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'auth';
import { useConfig } from 'providers/config/ConfigProvider';
import { AppBarAvatar } from '../../atoms/app-bar-avatar/AppBarAvatar';
import { Theme } from '../../atoms/theme/Theme';
import { useWindowDimensions } from 'hooks/useGetWindowDimensions';
import { Backdrop } from '../../atoms/backdrop/Backdrop';
import clsx from 'clsx';

const TOP_BAR_HEIGHT = 64;

export const ContextMenu = () => {
  const authApi = useAuth();
  const env = useConfig();
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const identificationState = authApi.identificationState;
  const currentSignedInUser = authApi.getSignedInUser();
  const anchorElement = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const { height } = useWindowDimensions();
  const isUnidentified = identificationState === 'unidentified';

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const logIn = async () => {
    await authApi.login({ authSuccessRedirect: location.pathname + location.search });
  };

  const logOut = async () => {
    closeMenu();
    await authApi.logout();
  };

  const getLoggedInUserName = (): string => currentSignedInUser?.displayName ?? '';
  const getLoggedInEmail = (): string => currentSignedInUser?.email ?? '';
  if (isUnidentified) {
    return (
      <Theme variant='secondary'>
        <Button variant={BUTTON_VARIANT.PRIMARY} size={BUTTON_SIZE.MEDIUM} onClick={logIn}>
          Log in
        </Button>
      </Theme>
    );
  }

  return (
    <>
      <div>
        <div
          className={styles.userInfoSectionLoggedIn}
          ref={anchorElement}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <AppBarAvatar />
          <div className={styles.dropdownIcon}>
            {menuOpen ? (
              <Icon icon='navigation_expand-less' />
            ) : (
              <Icon icon='navigation_expand-more' />
            )}
          </div>
        </div>
      </div>
      <div className={styles.noPointer}>
        <Backdrop isOpen={menuOpen} onClick={() => setMenuOpen(false)} disableBackdropColor />
        <Popover
          aria-label='User context menu'
          open={menuOpen}
          position={Popover.POSITION.BOTTOM_END}
          className={styles.userInfoContextMenu}
          disablePadding
          anchorEl={anchorElement.current as HTMLElement}
          maxHeight={height - TOP_BAR_HEIGHT}
          mobileModal={false}
        >
          <div
            className={clsx(styles.exitBlock, styles.contextMenuBaseSidePadding)}
            onClick={() => closeMenu()}
          >
            <Icon icon='content_clear' className={styles.xxLargeIcon} />
          </div>
          <div className={clsx(styles.userBlock, styles.contextMenuLargeSidePadding)}>
            <div className={styles.userBlockContent}>
              <div>
                <Typography
                  align={Typography.ALIGN.LEFT}
                  variant={Typography.VARIANT.B2}
                  weight={Typography.WEIGHT.NORMAL}
                  color={Typography.COLOR.INHERIT}
                >
                  <strong>{getLoggedInUserName()}</strong>
                </Typography>
                <Typography
                  align={Typography.ALIGN.LEFT}
                  variant={Typography.VARIANT.B4}
                  weight={Typography.WEIGHT.NORMAL}
                  color={Typography.COLOR.INHERIT}
                >
                  {getLoggedInEmail()}
                </Typography>
              </div>
            </div>
          </div>
          <div className={clsx(styles.displayUserIcon, styles.contextMenuLargeSidePadding)}>
            <Theme variant='secondary'>
              <IconButton
                aria-label='User Info'
                variant={IconButton.VARIANT.PRIMARY}
                size={IconButton.SIZE.X_LARGE}
              >
                <Icon icon='social-media_person' className={styles.xxLargeIcon} />
              </IconButton>
            </Theme>
          </div>
          <Divider size={Divider.SIZE.SMALL} />
          <MenuProvider>
            <div className={styles.contextMenuBaseSidePadding}>
              <MenuItem onClick={logOut}>
                <div className={styles.menuItemWithIcon}>
                  <Icon
                    icon='action_sign-out'
                    className={clsx(styles.menuIcon, styles.xLargeIcon)}
                  />
                  <Typography
                    align={Typography.ALIGN.LEFT}
                    variant={Typography.VARIANT.B2}
                    weight={Typography.WEIGHT.NORMAL}
                  >
                    Log out
                  </Typography>
                </div>
              </MenuItem>
              <div className={styles.rowVersionId}>
                <Typography
                  align={Typography.ALIGN.LEFT}
                  variant={Typography.VARIANT.B4}
                  weight={Typography.WEIGHT.NORMAL}
                  color={Typography.COLOR.INHERIT}
                >
                  Version {env.versionId}
                </Typography>
              </div>
            </div>
          </MenuProvider>
        </Popover>
      </div>
    </>
  );
};
